/* eslint-disable react/no-unknown-property */
import { Icon, Select, SelectOption } from '@pypestream/design-system';
import { Tenancy_Node_Type_Enum_Ps_Enum } from '@pypestream/api-services';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';

import { SelectPlaceholder } from './select-placeholder';
import { NodeColumns, TableRow, tableStore } from '../../stores/table-store';
import { capitalizeWords, transformActionFileData } from '../../utils';
import { useProStudioCtxSelector } from '../../xstate/app.xstate';
import { useFileInput, useUploadPythonFiles } from '../../hooks';

interface NodeCommandSelectProps {
  rowId: string;
  testId?: string;
}

export const NodeCommandSelect: React.FC<NodeCommandSelectProps> = observer(
  ({ rowId, testId }) => {
    const [isSelectRendered, setIsSelectRendered] = useState(false);
    const [cachedFunctions, setCachedFunctions] = useState<string[]>([]);
    const [addingNewFunction, setAddingNewFunction] = useState(false);
    const selectRef = useRef<React.ComponentRef<typeof Select>>(null);

    const { handleUpload } = useUploadPythonFiles('scriptFile');

    const { input, inputRef } = useFileInput({
      'test-id': `${testId}-input`,
      accept: '.py,.ts,.js',
      type: 'file',
      style: { display: 'none' },
      multiple: true,
      onChange: handleUpload,
    });

    const { functions } = useProStudioCtxSelector((ctx) => ({
      functions:
        ctx.draft?.files
          .filter((file) => file.startsWith('services/'))
          .map(transformActionFileData)
          .map((file) => file.name.replace(/\.[^.]+$/, '')) || [],
    }));

    const standardFunctions = [
      'SysMultiMatchRouting',
      'SysAssignVariable',
      'SysSetEnv',
      'SysShowMetadata',
      'SysVariableReset',
      'AsyncSysGoogleAICompletion',
      'SysSupervisorGetContext',
      'SysSupervisorRouting',
      'SysSupervisorProcessSelection',
      'ABTesting',
      'BotToPlatform',
      'HandleBotError',
    ];

    const searchData = [
      ...functions.map((name) => ({ id: name })),
      ...standardFunctions.map((name) => ({ id: name })),
    ];

    const nodeType = tableStore.getValue(rowId, NodeColumns.nodeType);
    const isDecision =
      nodeType === 'Decision' ||
      nodeType === 'decision' ||
      nodeType === Tenancy_Node_Type_Enum_Ps_Enum.D ||
      nodeType === 'd';

    const initialValue = tableStore.getValue(
      rowId,
      NodeColumns.command
    ) as TableRow[NodeColumns.command];

    const displayValue = initialValue ? capitalizeWords(initialValue) : '';

    useEffect(() => {
      if (isSelectRendered && selectRef.current) {
        // Use a longer delay to ensure the component is fully rendered
        setTimeout(() => {
          if (
            selectRef.current &&
            typeof selectRef.current.show === 'function'
          ) {
            selectRef.current.show();
            if (selectRef.current.focus) {
              selectRef.current.focus();
            }
          }
        }, 50);
      }
    }, [isSelectRendered]);

    const onFileInputClick = () => {
      inputRef.current?.click();
      setCachedFunctions(functions);
      setAddingNewFunction(true);
    };

    useEffect(() => {
      if (functions.length > cachedFunctions.length && addingNewFunction) {
        const newFunc = functions.filter(
          (func) => !cachedFunctions.includes(func)
        )?.[0];

        tableStore.updateRow({
          rowId,
          updates: {
            [NodeColumns.command]: newFunc,
          },
          skipValidationRules: false,
          saveImmediately: true,
        });

        tableStore.clearCellValidationError(rowId, NodeColumns.command);

        setCachedFunctions(functions);
        setAddingNewFunction(false);
      }
    }, [functions, cachedFunctions, rowId, addingNewFunction]);

    if (isDecision) {
      return undefined;
    }

    let content = null;

    if (tableStore.isEditing()) {
      if (!isSelectRendered) {
        const hasError = !searchData.some((item) => item.id === displayValue);
        const defaultPlaceholderStyle = {};
        const placeholderStyle = hasError
          ? { ...defaultPlaceholderStyle, color: '#a13337' }
          : defaultPlaceholderStyle;

        content = (
          <SelectPlaceholder
            testId={`${testId}-placeholder`}
            onOpen={() => setIsSelectRendered(true)}
            ariaLabel="Open command selection"
            style={placeholderStyle}
          >
            <span>{displayValue}</span>
          </SelectPlaceholder>
        );
      } else {
        content = (
          <>
            <Select
              ref={selectRef}
              value={initialValue}
              test-id={testId}
              size="small"
              variant="outlined"
              data={searchData}
              search-keys={['id']}
              style={{ width: '100%' }}
              autoSizePadding={36}
              disabled={isDecision}
              onclose={() => {
                setTimeout(() => {
                  tableStore.isCellBeingEdited = false;
                  setIsSelectRendered(false);
                }, 100);
              }}
              onChange={(e) => {
                const newValue = String(
                  (e.target as unknown as { value?: string }).value || ''
                );

                if (!newValue) {
                  return;
                }

                if (
                  [...functions, ...standardFunctions].includes(
                    e.target.value as string
                  )
                ) {
                  // Dropdowns should save immediately
                  tableStore.updateRow({
                    rowId,
                    updates: {
                      [NodeColumns.command]: newValue as string,
                    },
                    skipValidationRules: false,
                    saveImmediately: true,
                  });
                } else {
                  // Dropdowns should save immediately
                  tableStore.updateRow({
                    rowId,
                    updates: {
                      [NodeColumns.command]: undefined,
                    },
                    skipValidationRules: false,
                    saveImmediately: true,
                  });
                }

                tableStore.clearCellValidationError(rowId, NodeColumns.command);

                const closestCell = e.target.closest('td');
                if (closestCell) {
                  closestCell.focus();
                  setTimeout(() => {
                    tableStore.isCellBeingEdited = false;
                  }, 100);
                }
              }}
              placement="bottom-start"
              className="c-editor-table__command-cell-select"
            >
              {/* Action Nodes section */}
              <SelectOption
                key="section-action"
                value="__add_new__"
                selectable={false}
                onclick={onFileInputClick}
                className="c-editor-table-command-select__option"
              >
                <span
                  style={{
                    fontWeight: 800,
                    textDecoration: 'underline',
                    color: '#000',
                    fontSize: '12px',
                  }}
                >
                  Functions
                </span>
                <Icon name="plus" size="xsmall" slot="suffix" />
              </SelectOption>
              {[...functions, ...standardFunctions]
                .sort((a, b) => a.localeCompare(b))
                .map((name) => (
                  <SelectOption
                    key={`action-${name}`}
                    value={name}
                    className="c-editor-table-command-select__option"
                  >
                    <span style={{ fontSize: '12px' }}>{name}</span>
                  </SelectOption>
                ))}
            </Select>
          </>
        );
      }

      return (
        <>
          {content}
          {input}
        </>
      );
    }

    return <>{tableStore.getValue(rowId, NodeColumns.command)}</>;
  }
);
